/**
 * "Jump back in" (2026-10-05, app-study.html's home): the last few things the
 * person touched - a timeline they edited, a control they set up, their
 * lights - so Navigate can put them one tap away.
 *
 * Per device, in browser storage (a convenience, never synced: the iPad and
 * the laptop are used for different work). Bounded to the newest MAX entries;
 * a repeat moves to the front instead of adding a line. Every storage touch is
 * wrapped, so private mode never breaks Navigate.
 */

import { createSignal, type Accessor } from 'solid-js';

const KEY = 'opbuttons.navigate.activity.v1';
const MAX = 8;

export type ActivityKind = 'timeline' | 'control' | 'lights';

export interface Activity {
  kind: ActivityKind;
  /** Timeline id, item id, or 'lights'. */
  id: string;
  title: string;
  /** For a control: the page it is on, by id and name. */
  pageId?: string;
  pageName?: string;
  projectId?: string;
  at: number;
}

function load(): Activity[] {
  try {
    const raw = localStorage.getItem(KEY);
    const v = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? (v as Activity[]) : [];
  } catch {
    return [];
  }
}

const [list, setList] = createSignal<Activity[]>(load());
export const recentActivity: Accessor<Activity[]> = list;

export function recordActivity(a: Omit<Activity, 'at'>): void {
  const next = [{ ...a, at: Date.now() }, ...list().filter((x) => !(x.kind === a.kind && x.id === a.id))].slice(0, MAX);
  setList(next);
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* convenience only */ }
}
